/**
 * Explicit success/failure for operations that fail for *expected* reasons —
 * a malformed upload, a parser rejection. Programmer errors still throw.
 * See CODE_RULES.md §5.
 */

export type Result<T, E = AppError> = { ok: true; value: T } | { ok: false; error: E };

export interface AppError {
  /** Stable, machine-readable. Never shown to the user verbatim. */
  readonly code: AppErrorCode;
  /** Safe to display: no paths, no stack traces, no internals. */
  readonly message: string;
  readonly cause?: unknown;
}

export type AppErrorCode =
  | "FILE_TOO_LARGE"
  | "FILE_EMPTY"
  | "FILE_WRONG_TYPE"
  | "FILE_UNREADABLE"
  | "IFC_PARSE_FAILED"
  | "PDF_PARSE_FAILED"
  | "VIEWER_INIT_FAILED";

export function ok<T>(value: T): Result<T, never> {
  return { ok: true, value };
}

export function err<E>(error: E): Result<never, E> {
  return { ok: false, error };
}

export function appError(code: AppErrorCode, message: string, cause?: unknown): AppError {
  return cause === undefined ? { code, message } : { code, message, cause };
}

/**
 * Narrows an unknown thrown value to something loggable without leaking
 * internals into UI copy.
 */
export function describeUnknown(value: unknown): string {
  if (value instanceof Error) return value.message;
  if (typeof value === "string") return value;
  return "An unexpected error occurred.";
}
